/**
 * newDealSheet.ts — the New Deal sheet's pure rules (slice 1, RULED
 * 2026-10-06), in their own module so SalesPipeline.tsx keeps exporting
 * components only (react-refresh) and so each rule is testable alone.
 */
import { money, parsePoundsToPence } from '../../../../lib/money'
import { fmtDate } from '../../api'
import { quarterLabel } from './newDealCloseDate'

/** The Create gate (slice 1, ruled): company, title and the value field
 *  non-empty. Exported for its own tests. */
export function newDealCreatable(company: string, title: string, value: string): boolean {
  return company.trim() !== '' && title.trim() !== '' && value.trim() !== ''
}

/** Separators on blur, through the existing money formatter: the amount is
 *  re-rendered by money() without its symbol, and kept ONLY if it parses
 *  back to the same pence — a locale whose separators the parser does not
 *  read leaves the typed text untouched rather than corrupting it. */
export function withSeparators(raw: string): string {
  const pence = parsePoundsToPence(raw)
  if (pence === null) return raw
  const shown = money(pence / 100, 'GBP').replace(/^£/, '')
  return parsePoundsToPence(shown) === pence ? shown : raw
}

/** Slice 3 (F1): the footer's live summary, rebuilt on every change. The
 *  category is "Uncategorised" because that is what a created deal IS — the
 *  create carries no category (P2-9: a category is human-set). A deal with
 *  no close date or no owner says so rather than printing a placeholder. */
export function newDealSummary(o: { stage: string; closeIso: string; ownerName: string | null }): string {
  const parts = [`Adds to ${o.stage}`, 'forecast Uncategorised']
  parts.push(o.closeIso ? `closes ${fmtDate(`${o.closeIso}T12:00:00Z`)} (${quarterLabel(o.closeIso)})` : 'no close date')
  // ownerName: '' = no owner; null = an owner whose name is not known here,
  // so the segment is left out rather than claim either.
  if (o.ownerName !== null) parts.push(o.ownerName ? `owned by ${o.ownerName}` : 'no owner')
  return parts.join(' · ')
}

/** Slice 3 (S10): an unsaved New Deal draft survives an accidental close for
 *  this long, and no longer. THIS form's rule — draftStorage itself is
 *  unchanged, and every other form's draft keeps its old behaviour. */
export const NEW_DEAL_DRAFT_TTL_MS = 10 * 60_000

/** Whether a stored draft may still be offered. A draft with no savedAt
 *  stamp (written before this rule) or one stamped in the future cannot be
 *  shown to be under ten minutes old, so it is not offered. */
export function draftFresh(savedAt: unknown, nowMs: number): boolean {
  if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return false
  const age = nowMs - savedAt
  return age >= 0 && age <= NEW_DEAL_DRAFT_TTL_MS
}
